<script lang="ts" setup>
  import { ref, computed, provide, watch } from "vue";
  import type { MembershipTier } from "@/models/membershipTier";
  import { useSessionStore } from "@/stores/session";
  import { useAppStore } from "@/stores/app";
  import SimpleModal from "@/components/modals/SimpleModal.vue";
  import MembershipTierListWithTitle from "@/components/membership/MembershipTierListWithTitle.vue";
  import * as amplitude from "@amplitude/analytics-browser";
  import { type MembershipTierPlan, MembershipTierPlanFrequency } from '@/models/membershipTierPlan'
  import MembershipPriceDetails from "@/components/membership/MembershipPriceDetails.vue";

  const props = withDefaults(defineProps<{
    membershipTier: MembershipTier;
    membershipTierPlan?: MembershipTierPlan;
    changeTierFn: (() => void) | null;
  }>(), {
    changeTierFn: null,
  });

  const emit = defineEmits<{
    "update": [tierId: number, tierPlanId?: number];
  }>();

  const sessionStore = useSessionStore();
  const appStore = useAppStore();

  // const previousTier = computed(() => {
  //   if (position.value > 0) {
  //     return appStore.membershipTiers[ position.value - 1 ];
  //   }
  //   return null;
  // });

  const showTierSelectionModal = ref<boolean>(false);
  watch(showTierSelectionModal, (value) => {
    if (!value) {
      amplitude.track('Tier Selection Modal Closed');
    }
  });

  function handleChangeTierClick() {
    amplitude.track('Change Tier Clicked', {
      "Tier Name": props?.membershipTier?.name ?? "",
      "Tier Price": props?.membershipTier?.price < 0 ? 'Roundup' : props?.membershipTierPlan?.price ?? '',
      "Tier Cycle": props?.membershipTierPlan?.frequency ?? MembershipTierPlanFrequency.MONTH,
    });

    if (props.changeTierFn) {
      props.changeTierFn();
    } else {
      showTierSelectionModal.value = true;
    }
  }

  provide('selectMembershipTier', (tierId: number, tierPlanId?: number) => {
    showTierSelectionModal.value = false;
    emit('update', tierId, tierPlanId);
  });

</script>

<template>
  <div class="tier-select">
    <div class="row">
      <span class="t-body-1 t-bold">{{ $t('signup.single-page.billing.new.plan') }}</span>
      <span class="t-body-1 t-bold">-</span>
      <v-btn variant="plain" @click="handleChangeTierClick">{{ $t('common.switch') }}</v-btn>
    </div>
    <div class="row details">
      <span class="t-body-1">{{ membershipTier.labels[ sessionStore.language ] }}</span>
      <MembershipPriceDetails :membershipTier="membershipTier" :membershipTierPlan="membershipTierPlan" />
    </div>
  </div>

  <SimpleModal
    v-if="changeTierFn == null"
    compact
    :model-value="showTierSelectionModal"
    @close="showTierSelectionModal = false"
  >
    <div class="tier-list">
      <MembershipTierListWithTitle :title="$t(`signup.single-page.tier.title`)"></MembershipTierListWithTitle>
    </div>
    <div class="toc t-body-2">
      <span>{{ $t('sign-up.toc.first') }}</span>
      <router-link to="/support/terms" target="_blank">{{ $t('sign-up.toc.btn.toc') }}</router-link>
      <span>{{ $t('sign-up.toc.second') }}</span>
      <router-link to="/support/privacy" target="_blank">{{ $t('sign-up.toc.btn.privacy') }}</router-link>
    </div>
  </SimpleModal>
</template>

<style lang="scss" scoped>
  .tier-select {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    :deep(.v-btn) {
      padding: 0;
      opacity: 1;
      min-width: unset;
      height: 21px;
      font-weight: getFontWeight('medium');
    }

    .row {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      &.details {
        justify-content: space-between;

        :deep(.price) {
          text-align: end;

          .value {
            font-size: unset;
            font-weight: getFontWeight('medium');
          }
        }
      }

    }
  }

  .toc {
    text-align: center;

    a {
      color: rgb(var(--v-theme-primary));
      margin: 0 5px;
    }
  }

  @media (max-width: breakpoint(md) - 1px) {
    .toc {
      margin-top: 0.5rem;
    }
  }
</style>
